import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "../context/LangContext";

const LINK_IDS = ["home", "services", "projects", "about", "partners", "contact"];

export const scrollToSection = (id) => {
  const target = `#${id}`;
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 inset-x-0 z-50 border-b bg-[#FBF6E9]/90 backdrop-blur-xl border-[#E8DFC5] shadow-[0_8px_30px_rgba(20,16,10,0.06)]"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 min-h-[72px] py-2.5 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
        >
          <img
            src="/images/logo.png"
            alt="شعار شركة فكرة بغداد"
            className="w-11 h-11 object-contain transition-transform duration-300 group-hover:rotate-3"
          />
          <span className="text-right leading-tight">
            <span className="block font-heading font-extrabold text-lg text-stone-900">شركة فكرة بغداد</span>
            <span className="block text-[10px] text-stone-500 mt-0.5 whitespace-nowrap">
              للمقاولات والتجارة العامة والاستثمارات الصناعية محدودة المسؤولية
            </span>
            <span className="block font-accent text-[11px] tracking-[0.18em] text-emerald-600 mt-1">
              FIKRAT BAGHDAD COMPANY
            </span>
            <span className="block font-accent text-[8px] tracking-wide text-stone-500 mt-0.5 max-w-[240px] leading-relaxed">
              For General Contracting, Trading and Industrial Investments Limited Liability
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-menu">
          {LINK_IDS.map((id) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className="relative text-sm font-medium text-stone-600 hover:text-emerald-600 transition-colors duration-300 py-2 after:absolute after:bottom-0 after:right-0 after:h-px after:w-0 after:bg-emerald-400 after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {t.nav[id]}
            </button>
          ))}
          <button
            data-testid="nav-lang-toggle"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E8DFC5] px-4 py-2 text-sm font-bold text-stone-600 transition-all duration-300 hover:border-emerald-500 hover:text-emerald-600"
            aria-label="Switch language"
          >
            <Languages className="w-4 h-4" />
            {lang === "ar" ? "EN" : "عربي"}
          </button>
          <button
            data-testid="nav-cta-button"
            onClick={() => go("contact")}
            className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_24px_rgba(16,185,129,0.4)]"
          >
            {t.nav.cta}
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-stone-700 p-2"
          aria-label="القائمة"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-[#FBF6E9]/95 backdrop-blur-xl border-b border-[#E8DFC5]"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINK_IDS.map((id) => (
                <button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  onClick={() => go(id)}
                  className="text-right py-3 px-2 text-base text-stone-700 hover:text-emerald-600 hover:bg-white rounded-md transition-colors duration-200"
                >
                  {t.nav[id]}
                </button>
              ))}
              <button
                data-testid="nav-mobile-lang-toggle"
                onClick={() => setLang(lang === "ar" ? "en" : "ar")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-[#E8DFC5] px-4 py-3 text-sm font-bold text-stone-600"
              >
                <Languages className="w-4 h-4" />
                {lang === "ar" ? "English" : "العربية"}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
